<script>
import { EventEmitTypes, events, HotAreaEventListenerTypes, getHotAreaId, emitHotArea, generateHotareaActionParams, deleteHotAreaNode, setHotAreaNode, generateHandlers } from '@tencent/gems-shared';

const LONG_PRESS_TIMEOUT = 1000;
const DOUBLE_CLICK_TIMEOUT = 200;
const INTERSECTION_TIMEOUT = 500;

export default {
  props: ['instance', 'hotArea', 'meta', 'global'],
  data: function () {
    return {
      doubleClickTimer: null,
      preventClick: false,
      handlerParams: {},
      handlers: {},
      longPressTimer: null,
      intersectionTimer: null,
    };
  },
  created: function () {
    this.handlerParams = { global: this.global, meta: this.meta };
    this.updateHandlersWithParams(this.hotArea.actions);
  },
  mounted: function () {
    setHotAreaNode(this.$refs.containerRef);

    this.initIntersectionObserver();
    this.observeIntersection();

    events.on(EventEmitTypes.HOT_AREA_INTERSECTING_CHANGE, this.onHotAreaIntersectionChange);

    if (this.handlers.onInit) {
      this.handlers.onInit(undefined);
    }
    this.execActionHooks(this.hotArea.actions);
  },
  beforeUpdate: function () {
    this.updateHandlerParams(this.meta, this.global);
    this.updateHandlersWithParams(this.hotArea.actions);
  },
  beforeDestory: function () {
    this.unobserveIntersection();

    events.cancel(EventEmitTypes.HOT_AREA_INTERSECTING_CHANGE, this.onHotAreaIntersectionChange);

    deleteHotAreaNode(this.hotArea.kebabCase);

    if (this.longPressTimer) {
      window.clearTimeout(this.longPressTimer);
    }
  },
  methods: {
    onHotAreaIntersectionChange: function (isIntersecting, key) {
      if (key !== this.hotArea.key) {
        return;
      }

      if (this.intersectionTimer) {
        clearTimeout(this.intersectionTimer);
        this.intersectionTimer = null;
      }

      const callback = isIntersecting ? this.onHotAreaEnterView : this.onHotAreaLeaveView;

      this.intersectionTimer = window.setTimeout(() => {
        callback();
        clearTimeout(this.intersectionTimer);
        this.intersectionTimer = null;
      }, INTERSECTION_TIMEOUT);
    },

    onHotAreaEnterView: function () {
      if (this.handlers.onEnterView) {
        this.handlers.onEnterView(undefined);
      }
    },

    onHotAreaLeaveView: function () {
      if (this.handlers.onLeaveView) {
        this.handlers.onLeaveView(undefined);
      }
    },
    execActionHooks: function (actions) {
      actions.forEach((action) => {
        if (typeof action.action.action === 'function') {
          return;
        }

        const hooks = action.action.action;

        if (hooks.afterHandlerRender) {
          const params = generateHotareaActionParams(action, this.hotArea, this.instance, this.global, this.meta);
          try {
            hooks.afterHandlerRender(params);
          } catch (e) {
            console.error('Action "afterHandlerRender" hook throw error: ', e);
          }
        }
      });
    },
    initIntersectionObserver: function () {
      this.intersectionObserver = new window.IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          const key = parseInt(target.getAttribute('data-key'), 10);
          events.emit(EventEmitTypes.HOT_AREA_INTERSECTING_CHANGE, isIntersecting, key);
        });
      });
    },
    onHotAreaPress: function (e) {
      if (!this.handlers.onLongPress) {
        return;
      }
      this.longPressTimer = window.setTimeout(() => this.handlers.onLongPress(e), LONG_PRESS_TIMEOUT);
    },
    onHotAreaPressRelease: function () {
      if (this.longPressTimer) {
        window.clearTimeout(this.longPressTimer);
      }
      this.longPressTimer = null;
    },
    observeIntersection: function () {
      this.intersectionObserver.observe(this.$refs.containerRef);
    },
    unobserveIntersection: function () {
      this.intersectionObserver.unobserve(this.$refs.containerRef);
    },
    onClick: function (e) {
      this.doubleClickTimer = window.setTimeout(() => {
        if (!this.preventClick) {
          try {
            this.handlers.onClick(e);
          } catch (e) {
            console.error('Exec onClick action error:', e);
          }
        }
      }, DOUBLE_CLICK_TIMEOUT);
    },
    onDoubleClick: function (e) {
      if (this.doubleClickTimer) {
        clearTimeout(this.doubleClickTimer);
        this.doubleClickTimer = null;
      }

      this.preventClick = true;
      try {
        this.handlers.onDoubleClick(e);
      } catch (e) {
        console.error('Exec onClick action error:', e);
      }

      setTimeout(() => (this.preventClick = false), DOUBLE_CLICK_TIMEOUT);
    },
    emitHotAreaEvent: function (e, type) {
      const {
        instance,
        hotArea,
        handlerParams: { global, meta },
      } = this;
      return emitHotArea(e, type, hotArea, instance, meta, global);
    },
    withEmitHotAreaEvent: function (type, handler) {
      const self = this;
      return function (e) {
        self.emitHotAreaEvent(e, type);
        if (handler) {
          return handler(e, self.handlerParams);
        }
      };
    },
    updateHandlerParams: (meta, global) => {
      this.handlerParams.meta = meta;
      this.handlerParams.global = global;
    },
    updateHandlersWithParams: function (actions) {
      const {
        onInit,
        onClick,
        onMouseEnter,
        onMouseLeave,
        onDoubleClick,
        onLongPress,
        onEnterView,
        onLeaveView,
      } = generateHandlers(actions, this.instance, this.hotArea);

      const { withEmitHotAreaEvent, handlers } = this;

      handlers.onInit = withEmitHotAreaEvent(HotAreaEventListenerTypes.INIT, onInit);
      handlers.onClick = withEmitHotAreaEvent(HotAreaEventListenerTypes.CLICK, onClick);
      handlers.onMouseEnter = withEmitHotAreaEvent(HotAreaEventListenerTypes.MOUSE_ENTER, onMouseEnter);
      handlers.onMouseLeave = withEmitHotAreaEvent(HotAreaEventListenerTypes.MOUSE_LEAVE, onMouseLeave);
      handlers.onDoubleClick = withEmitHotAreaEvent(HotAreaEventListenerTypes.DOUBLE_CLICK, onDoubleClick);
      handlers.onLongPress = withEmitHotAreaEvent(HotAreaEventListenerTypes.LONG_PRESS, onLongPress);
      handlers.onEnterView = withEmitHotAreaEvent(HotAreaEventListenerTypes.ENTER_VIEW, onEnterView);
      handlers.onLeaveView = withEmitHotAreaEvent(HotAreaEventListenerTypes.LEAVE_VIEW, onLeaveView);
    },
  },
  render: function (createElement) {
    const { onMouseLeave, onMouseEnter } = this.handlers;

    return createElement(
      'div',
      {
        class: '__impage-hot-area-actions-proxy',
        attrs: {
          id: getHotAreaId(this.hotArea.key),
          'data-key': this.hotArea.key,
        },
        ref: 'containerRef',
        style: this.style,
        on: {
          click: this.onClick,
          dblclick: this.onDoubleClick,
          mouseenter: onMouseEnter,
          mouseleave: onMouseLeave,
          touchstart: this.onHotAreaPress,
          touchend: this.onHotAreaPressRelease,
          mousedown: this.onHotAreaPress,
          mouseup: this.onHotAreaPressRelease,
        },
      },
      this.$slots.default,
    );
  },
};
</script>
